/**
 * Cor de sinal ↔ modulação, única fonte de verdade.
 *
 * Duas famílias de função porque canvas 2D não resolve var(--x) em
 * fillStyle — precisa de rgb() concreto — enquanto DOM/inline-style resolve
 * var() normalmente e deve usá-lo, pra herdar troca de token sem recompilar.
 */

const STOP_VARS = ['--wf-frio', '--wf-medio', '--wf-quente', '--wf-pico'] as const
// Mesmos valores hoje escritos em tokens.css — só entram em jogo se
// getComputedStyle não resolver (ex. ambiente sem o CSS carregado).
const STOP_FALLBACK: readonly string[] = ['#16304a', '#2f7a6b', '#d8a13c', '#ef5f5f']

let stopsCache: [number, number, number][] | null = null

function hexParaRGB(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  const grupo = m?.[1]
  if (!grupo) return [0, 0, 0]
  const n = parseInt(grupo, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function lerStops(): [number, number, number][] {
  if (stopsCache) return stopsCache
  const root = typeof document !== 'undefined' ? getComputedStyle(document.documentElement) : null
  stopsCache = STOP_VARS.map((nome, i) => {
    const bruto = root?.getPropertyValue(nome).trim()
    return hexParaRGB(bruto || STOP_FALLBACK[i] || '#000000')
  })
  return stopsCache
}

/** v em [0,1] (0 = piso de ruído/silêncio, 1 = pico). Interpola pelos 4
 * stops do waterfall. Uso em canvas: escreve direto num ImageData. */
export function nivelParaRGB(v: number): [number, number, number] {
  const stops = lerStops()
  const x = Math.max(0, Math.min(1, v)) * (stops.length - 1)
  const i = Math.min(stops.length - 2, Math.floor(x))
  const t = x - i
  const a = stops[i] ?? [0, 0, 0]
  const b = stops[i + 1] ?? a
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ]
}

/** Mesma interpolação, pronta como string "rgb(r, g, b)" pro fillStyle de
 * canvas (SMeter) ou pra um style.color de nó DOM já calculado (dBm). */
export function nivelParaCor(v: number): string {
  const [r, g, b] = nivelParaRGB(v)
  return `rgb(${r}, ${g}, ${b})`
}

/** mode em qualquer caixa ("FM","am","USB",...). Retorna var(--x) pronto
 * pra style inline de DOM — NÃO usar em canvas, ver corDoModoCanvas. */
export function corDoModo(mode: string): string {
  switch (mode.toUpperCase()) {
    case 'FM':
      return 'var(--dial)'
    case 'AM':
      return 'var(--wf-quente)'
    case 'CW':
      return 'var(--modo-cw)'
    case 'USB':
    case 'LSB':
    case 'SSB':
      return 'var(--letra-2)'
    case 'DATA':
    case 'DIGITAL':
    case 'DMR':
    case 'C4FM':
    case 'D-STAR':
      return 'var(--digital)'
    default:
      return 'var(--letra-2)'
  }
}

const VAR_PARA_HEX: Record<string, string> = {
  '--dial': '#e0913a',
  '--wf-quente': '#d8a13c',
  '--modo-cw': '#6fa8dc',
  '--letra-2': '#a3adb9',
  '--digital': '#5fd3d3',
}

const canvasCache = new Map<string, string>()

/** Mesma tabela de corDoModo, resolvida pra rgb() concreto — uso em canvas
 * (marcadores de repetidora do DialStrip). */
export function corDoModoCanvas(mode: string): string {
  const varRef = corDoModo(mode)
  const cached = canvasCache.get(varRef)
  if (cached) return cached

  const nome = /var\((--[\w-]+)\)/.exec(varRef)?.[1]
  const root = typeof document !== 'undefined' && nome ? getComputedStyle(document.documentElement) : null
  const bruto = (nome && root?.getPropertyValue(nome).trim()) || (nome && VAR_PARA_HEX[nome]) || '#8a939e'
  canvasCache.set(varRef, bruto)
  return bruto
}

/** Converte "S7" ou "S9+20" no número de unidades S. Compartilhada entre o
 * S-meter (segmentos) e o RXReadout (cor do texto). */
export function parseSUnits(label: string): number {
  const m = /^S(\d+)(?:\+(\d+))?$/.exec(label)
  if (!m) return 0
  const base = Number(m[1])
  const over = m[2] ? Number(m[2]) / 6 : 0
  return base + over
}
